import { useEffect, useState } from "react"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { fetchQuery, graphql } from "react-relay"
import { getServiceMapping } from "@/lib/fetch-service-mappings"
import { getPersistedLanguage } from "@/lib/language-storage"
import { selectContentForLanguage } from "@/lib/select-content-for-language"
import {
  preventionProgramsSectionChQuery,
  preventionProgramsSectionChQuery$data,
} from "@/modules/patients/agenda/teleconsultation/booking/__generated__/preventionProgramsSectionChQuery.graphql"
import {
  preventionProgramsSectionDeQuery,
  preventionProgramsSectionDeQuery$data,
} from "@/modules/patients/agenda/teleconsultation/booking/__generated__/preventionProgramsSectionDeQuery.graphql"
import { TeleconsultationForm } from "@/modules/patients/agenda/teleconsultation/booking/teleconsultation-schema"
import { MultipleSelect } from "@/modules/patients/components/multiple-select"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { RelayEnvironment } from "@/relay-environment"
import { Country } from "@/types/__generated__/union-types"

const chPreventionProgramsQuery = graphql`
  query preventionProgramsSectionChQuery($search: String!) {
    preventionPrograms: ch_preventionPrograms(search: $search) {
      edges {
        node {
          id
          key
          name {
            en
            de
            fr
            it
          }
          specialty
        }
      }
    }
  }
`

const dePreventionProgramsQuery = graphql`
  query preventionProgramsSectionDeQuery($search: String!) {
    preventionPrograms: de_preventionPrograms(search: $search) {
      edges {
        node {
          id
          key
          name {
            en
            de
            fr
            it
          }
          specialty
        }
      }
    }
  }
`

type PreventionProgramsData = preventionProgramsSectionChQuery$data | preventionProgramsSectionDeQuery$data

const fetchPreventionPrograms = (country: Country, next: (data: PreventionProgramsData) => void) => {
  const query = country === "CH" ? chPreventionProgramsQuery : dePreventionProgramsQuery
  return fetchQuery<preventionProgramsSectionChQuery | preventionProgramsSectionDeQuery>(
    RelayEnvironment,
    query,
    { search: "" },
    { fetchPolicy: "store-or-network" },
  ).subscribe({ next })
}

export const PreventionProgramsSection = () => {
  const { t } = useTranslation()
  const language = getPersistedLanguage()
  const { control, setValue, getValues } = useFormContext<TeleconsultationForm>()
  const serviceWatch = useWatch({ control, name: "service" })
  const selectedKeys = useWatch({ control, name: "preventionProgramKeys" })
  const countryCode = useCountryParam()
  const [preventionPrograms, setPreventionPrograms] = useState<PreventionProgramsData | null>(null)

  useEffect(() => {
    if (serviceWatch === "PREVENTION_ASSESSMENT" && !preventionPrograms) {
      const subscription = fetchPreventionPrograms(countryCode, setPreventionPrograms)
      return () => subscription.unsubscribe()
    }
  }, [serviceWatch, countryCode, preventionPrograms])

  // When the related prevention program changes (manual selection, or the banner preselection once the
  // program list loads), preselect the appointment specialty from it — but only if none is chosen yet and
  // exactly one program is related. Specialty is read via getValues (not watched), so a manual specialty
  // change, including resetting it back to NONE, is never overridden by this effect.
  useEffect(() => {
    if (serviceWatch !== "PREVENTION_ASSESSMENT") return
    if (getValues("specialty") !== "NONE" || (selectedKeys ?? []).length !== 1) return

    const programSpecialty = preventionPrograms?.preventionPrograms?.edges?.find(
      (edge) => edge.node.key === selectedKeys[0],
    )?.node.specialty

    const isOfferedForPreventionAssessment = getServiceMapping().serviceMappings.some(
      (m) =>
        m.service === "PREVENTION_ASSESSMENT" &&
        m.specialty === programSpecialty &&
        m.isAllowedInCountries.includes(countryCode),
    )

    if (programSpecialty && programSpecialty !== "NONE" && isOfferedForPreventionAssessment) {
      setValue("specialty", programSpecialty)
    }
  }, [serviceWatch, selectedKeys, preventionPrograms, countryCode, setValue, getValues])

  const runningPrograms = (preventionPrograms?.preventionPrograms?.edges?.map((edge) => edge.node) ?? []).toSorted(
    (first, second) =>
      selectContentForLanguage(language, first.name).localeCompare(
        selectContentForLanguage(language, second.name),
        language,
      ),
  )

  if (serviceWatch !== "PREVENTION_ASSESSMENT" || runningPrograms.length === 0) return null

  return (
    <Controller
      control={control}
      name="preventionProgramKeys"
      render={({ field: { value, onChange } }) => (
        <MultipleSelect
          value={runningPrograms.filter((node) => (value ?? []).includes(node.key))}
          options={runningPrograms}
          getOptionLabel={(node) => selectContentForLanguage(language, node.name)}
          getOptionValue={(node) => node.key}
          onChange={(selected) => onChange(selected.map((node) => node.key))}
          label={t("patient.agenda.teleconsultationForm.preventionPrograms")}
          data-testid="prevention-programs-select"
        />
      )}
    />
  )
}
